
import { Component, Vue, Prop } from "vue-property-decorator"
import { Getter } from "vuex-class"

import { UserSettings } from "@/types/userSettings"
import ViewFolderItem from "./ViewFolderItem.vue"

import { Folder, FolderItem } from "@/types/folder"
import { folderMixin } from "@/mixins/folderMixin"

@Component({
	components: {
		"folder-item": ViewFolderItem,
		folders: () => import("./ViewFolders.vue")
	}
})
export default class ViewFolders<T extends FolderItem> extends Vue {
	@Prop()
	private readonly items!: T[]

	@Prop({ type: String, default: "Item" })
	private readonly descriptor!: string

	@Prop()
	private readonly activeItemId!: string | null;

	@Prop({ type: String, default: "" })
	private readonly searchFilter!: string;

	@Prop({ type: Array, default: () => ([]) })
	private readonly folders!: Folder[];

	@Prop({ type: Object, default: () => ({}) })
	private readonly parentFolder!: Folder;

	@Prop({ type: Number, default: 1 })
	private folderLevel!: number;

	@Getter("getUserSettings")
	public userSettings!: UserSettings;

	@Prop({ type: Boolean, default: true })
	readonly showGroupedUngroupedHeadings!: boolean

	@Prop({ type: String, default: undefined })
	readonly linkToPath!: string | undefined

	@Prop({ type: String, default: undefined })
	readonly linkToParamName!: string | undefined

	private currentSubFolderList: Folder[] = []

	private maxFolderLevel: number = 10
	private folderLevelPadding!: string

	// Increments the folder level count
	private get nextFolderLevel () {
		return this.folderLevel + 1
	}

	public get parsedFolders () {
		let folders: { [key: string]: (Folder)[]} = {}

		if (!this.parentFolder.id) {
			// Root folders
			// Filter the folders list to only include root folders
			const foldersFiltered: Folder[] = this.folders.filter(f => !f.parentFolderId || f.parentFolderId === null).sortBy("title")

			foldersFiltered.forEach(folder => {
				// If the current folder contains an item, push it to be displayed
				if (this.items.some(v => v.folder === folder.title)) {
					if (folders[folder.title]) {
						folders[folder.title].push(folder)
					} else {
						folders[folder.title] = [folder]
					}
				} else if (this.subFolderContainsView(folder)) {
					// If the current folder does not contain an item, check if any sub folders contain an item and if so, push the current folder to be displayed
					if (folders[folder.title]) {
						folders[folder.title].push(folder)
					} else {
						folders[folder.title] = [folder]
					}
				}
			})
		} else {
			// Direct sub folders to current parent
			// Filter the folders list to only include folders that have a parent
			const foldersFiltered: Folder[] = this.folders.filter(f => f.parentFolderId === this.parentFolder.id).sortBy("title")

			foldersFiltered.forEach(folder => {
				// Get the full path to root for the current folder
				let folderPath: string = folderMixin.methods.getPathToRootFolder(folder, this.folders, true)

				if (this.isDirectSubFolder(folderPath)) {
					// If an item is in the current folder, push it to be displayed
					if (this.items.some(v => v.folder === folderPath)) {
						if (folders[folder.title]) {
							folders[folder.title].push(folder)
						} else {
							folders[folder.title] = [folder]
						}
					} else {
						// If the current folder does not contain an item, check if any sub folders contain an item and if so, push the current folder to be displayed
						if (this.subFolderContainsView(folder)) {
							if (folders[folder.title]) {
								folders[folder.title].push(folder)
							} else {
								folders[folder.title] = [folder]
							}
						}
					}
				}
			})
		}

		return folders
	}

	private getFolderName (folderPath: string) {
		return folderMixin.methods.getFolderNameFromPath(folderPath)
	}

	// Checks if the provided folder is a direct sub folder to the current parent folder
	private isDirectSubFolder (folderPath: string): boolean {
		const folder = this.getFolder(folderPath)

		if (!folder) return false

		if (this.folders.firstOrDefault(f => f.title === this.parentFolder.title && f.id === folder.parentFolderId)) {
			return true
		} else {
			return false
		}
	}

	private subFolderContainsView (folder: Folder) {
		// Rebuild the sub folder list for the current folder
		this.currentSubFolderList = []
		this.buildSubFolderList(folder.id)

		// Folder has no sub folders
		if (this.currentSubFolderList.length < 1) {
			return false
		}

		// Check if any sub folders contain an item
		if (this.items.some(v => this.currentSubFolderList.find(f => folderMixin.methods.getPathToRootFolder(f, this.folders, true) === v.folder))) {
			return true
		} else {
			return false
		}
	}

	// Set the padding for folder names based off the current folder level
	private created () {
		// Set a max level so the padding doesn't run off screen
		const level: number = this.folderLevel <= this.maxFolderLevel ? this.folderLevel : this.maxFolderLevel
		this.folderLevelPadding = (20 * level).toString() + "px"
	}

	private getFolder (folderTitle: string): Folder {
		return folderMixin.methods.getFolderFromPath(folderTitle, this.folders)
	}

	private buildSubFolderList (folderId: string, excludeFirst: boolean = false) {
		// Create list of direct sub folders of provided folder ID
		let subFolderList: Folder[] = this.folders.filter(f => f.parentFolderId === folderId)

		// Loop through each sub folder, recursively getting any direct sub folders
		for (let i = 0, count = subFolderList.length; i < count; i++) {
			if (!excludeFirst) {
				// Add sub folder to the list
				this.currentSubFolderList.push(subFolderList[i])
			}

			// Get direct sub folders of current sub folder
			this.buildSubFolderList(subFolderList[i].id)
		}
	}

	public get ungroupedItems () {
		// Get items with no folder if no parent is provided, or items whose folder matches the provided parent folder
		return this.items.filter(v => {
			const noFolder =
				(!this.parentFolder.id && (!v.folder || v.folder === "")) ||
				(this.parentFolder.id && v.folder && this.getFolder(v.folder)?.id === this.parentFolder.id)

			const matchesSearchFilter = !this.searchFilter ? true : v.title.trim().toLowerCase().includes(this.searchFilter.trim().toLowerCase())

			return noFolder && matchesSearchFilter
		})
	}

	public getItem (itemId: string) {
		return this.items.find(item => item.id === itemId)
	}

	public getItemTitle (itemId: string) {
		return this.items.find(item => item.id === itemId)?.title ?? ""
	}

	public getCaseCount (item: T): number | null {
		return item.itemCount ?? null
	}

	public get expandedByDefault () {
		if (this.userSettings && this.userSettings.settings) {
			return !!this.userSettings.settings.foldersExpanded
		}
		return true
	}

	public folderIsActive (folder: string) {
		return this.getItem(this.activeItemId ?? "")?.folder === folder
	}

	get pluralizedDescriptor (): string {
		return this.descriptor.endsWith("s") ? this.descriptor : this.descriptor + "s"
	}
}
